import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  PawPrint, Calendar, Clock, Users, AlertTriangle, Megaphone, RefreshCw, Cat,
  ArrowRight, CheckCircle2, ShieldCheck, HeartHandshake,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/7474353/pexels-photo-7474353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  { icon: Calendar, title: "Shift scheduling", body: "Volunteers browse categories, pick from a calendar, and sign up in seconds. Approval-only tiers (like Woof Pack) built in." },
  { icon: Clock, title: "Automatic hour tracking", body: "One-tap check-in and check-out. Volunteers log which animals they cared for. Lifetime hours from your existing platform roll in automatically." },
  { icon: RefreshCw, title: "Multi-platform integrations", body: "Live-connect the volunteer and shelter management tools your team already uses — no CSV shuffling, no duplicate data entry." },
  { icon: AlertTriangle, title: "Animal issue reports", body: "Volunteers snap a photo, describe the concern, and your team gets an instant email — with severity, animal, and reporter." },
  { icon: Megaphone, title: "Shelter bulletin board", body: "Pin announcements to the volunteer home screen. Every volunteer sees what matters the moment they log in." },
  { icon: Cat, title: "Dogs and cats in the building today", body: "Volunteers see the current roster of adoptable animals, pulled live from your shelter management system." },
];

const VOLUNTEER_INTEGRATIONS = [
  { name: "Galaxy Digital", live: true },
  { name: "VolunteerHub", live: false },
  { name: "Better Impact", live: false },
  { name: "Volgistics", live: false },
];

const ANIMAL_INTEGRATIONS = [
  { name: "ShelterLuv", live: true },
  { name: "PetPoint", live: false },
  { name: "Chameleon", live: false },
];

const PHONE_SCREENS = [
  { src: "/screens/volunteer-home.jpg", caption: "Home & bulletins" },
  { src: "/screens/volunteer-shifts.jpg", caption: "Sign up for shifts" },
  { src: "/screens/volunteer-profile.jpg", caption: "Hours & animals" },
];

function PhoneFrame({ src, caption, rotation, delay }) {
  return (
    <div
      className="relative"
      style={{ transform: `rotate(${rotation}deg)`, animationDelay: `${delay}ms` }}
    >
      <div className="relative w-[220px] h-[460px] rounded-[36px] bg-ink shadow-2xl p-[6px] mx-auto ring-1 ring-black/10">
        {/* Notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-[80px] h-[18px] rounded-full bg-ink z-10" />
        {/* Screen */}
        <div className="relative w-full h-full rounded-[30px] overflow-hidden bg-sand">
          <img
            src={src}
            alt={caption}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-3 font-medium tracking-wide uppercase">{caption}</p>
    </div>
  );
}

function BrowserFrame({ src }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-white max-w-4xl mx-auto">
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-sand border-b border-border">
        <span className="w-2.5 h-2.5 rounded-full bg-terracotta/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-fern/60" />
        <span className="ml-3 text-[11px] text-muted-foreground font-mono">shelterfocus.app/admin</span>
      </div>
      <img src={src} alt="Admin dashboard" className="w-full block" loading="lazy" />
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-sand text-ink">
      {/* Nav */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PawPrint className="w-6 h-6 text-fern" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Shelter Focus</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-signin" className="rounded-full">Sign in</Button>
            </Link>
            <a href="#request">
              <Button data-testid="nav-request-demo" className="rounded-full bg-fern hover:bg-fern-dark text-white">
                Request a demo
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="label-caps mb-3">Volunteer management · Purpose-built for animal shelters</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
            Every shift <span className="text-fern">saves a life.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed">
            Shelter Focus is a volunteer management tool for animal shelters — one warm, simple app for shifts, hours, dog walks, cat cuddles, and shelter communication. It plugs into your existing volunteer and shelter software so you don&apos;t re-enter a single record.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#request">
              <Button data-testid="hero-cta" className="rounded-full h-12 px-6 bg-fern hover:bg-fern-dark text-white">
                Request a demo <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
              </Button>
            </a>
            <Link to="/login">
              <Button variant="outline" className="rounded-full h-12 px-6">
                Sign in to your shelter
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-fern" strokeWidth={1.5} /> No setup fee</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-fern" strokeWidth={1.5} /> Live in a week</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-fern" strokeWidth={1.5} /> Keeps your existing tools</span>
          </div>
        </div>
        <div className="relative">
          <img src={HERO_IMG} alt="Volunteer with dog" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
          <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-border max-w-xs">
            <p className="label-caps mb-1">Built for real shelters</p>
            <p className="text-sm">Managing <span className="font-semibold">484 volunteers</span> and <span className="font-semibold">25,000+ logged hours</span> today.</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <p className="label-caps mb-2">What&apos;s inside</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3 max-w-2xl">Everything your volunteer coordinator wishes existed.</h2>
          <p className="text-muted-foreground max-w-2xl mb-12">
            Built from the ground up with real shelter staff. No feature creep, no enterprise nonsense — just the tools that make Saturday morning easier.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="p-6 rounded-2xl border border-border hover:shadow-md transition">
                <div className="w-10 h-10 rounded-full bg-fern/10 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-fern" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot studio */}
      <section className="bg-sand border-b border-border overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="label-caps mb-2">See it in action</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3 max-w-2xl mx-auto">
              Made for phones. Made for volunteers.
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Real screens from the app your volunteers will actually use — no polished mockups, no marketing fiction.
            </p>
          </div>

          {/* Three phones — staggered */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 items-end mb-16 md:mb-20 justify-items-center">
            <PhoneFrame src={PHONE_SCREENS[0].src} caption={PHONE_SCREENS[0].caption} rotation={-4} delay={0} />
            <div className="md:-mt-8">
              <PhoneFrame src={PHONE_SCREENS[1].src} caption={PHONE_SCREENS[1].caption} rotation={0} delay={120} />
            </div>
            <PhoneFrame src={PHONE_SCREENS[2].src} caption={PHONE_SCREENS[2].caption} rotation={4} delay={240} />
          </div>

          {/* Admin desktop */}
          <div>
            <div className="text-center mb-8">
              <p className="label-caps mb-2">And your control room</p>
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold max-w-xl mx-auto">
                One dashboard for every shift, hour, and animal.
              </h3>
            </div>
            <BrowserFrame src="/screens/admin-dashboard.jpg" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-20">
        <p className="label-caps mb-2">From onboarding to Saturday morning</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10 max-w-2xl">Live in a week. No IT department required.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", title: "We import your volunteers", body: "Your existing volunteer roster and their lifetime hours flow in with one click. Nothing is lost." },
            { n: "02", title: "Your animals sync in", body: "Currently-available dogs and cats appear automatically from your shelter management system. Adopted animals disappear from the volunteer roster." },
            { n: "03", title: "One email launches the app", body: "Every volunteer gets a personalized welcome email with their own password-setup link. They tap it, set a password, and they're in." },
          ].map((s) => (
            <div key={s.n} className="p-6">
              <p className="font-heading text-4xl font-bold text-terracotta mb-2">{s.n}</p>
              <h3 className="font-heading text-lg font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Integrations */}
      <section className="bg-sand/60 border-y border-border">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center mb-10">
            <p className="label-caps mb-2">Works with what you already use</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3 max-w-2xl mx-auto">Bring your own volunteer &amp; shelter software.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Shelter Focus is a volunteer management tool — it doesn&apos;t replace the systems your shelter already relies on for
              animals and hours. We plug into the platforms your team already knows so nothing has to be re-entered.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="tactile-card border-0 p-6">
              <p className="label-caps mb-3">Volunteer management</p>
              <div className="flex flex-wrap gap-2">
                {VOLUNTEER_INTEGRATIONS.map((p) => (
                  <span key={p.name}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border ${p.live ? "bg-fern text-white border-fern" : "bg-white text-ink border-border"}`}
                    data-testid={`landing-vol-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    {p.name}
                    {p.live && <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />}
                    {!p.live && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Beta</span>}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">We pull your roster and lifetime hours so volunteers keep credit for what they&apos;ve already done.</p>
            </div>
            <div className="tactile-card border-0 p-6">
              <p className="label-caps mb-3">Shelter management</p>
              <div className="flex flex-wrap gap-2">
                {ANIMAL_INTEGRATIONS.map((p) => (
                  <span key={p.name}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border ${p.live ? "bg-fern text-white border-fern" : "bg-white text-ink border-border"}`}
                    data-testid={`landing-animal-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    {p.name}
                    {p.live && <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />}
                    {!p.live && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Beta</span>}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">We pull your currently-available animals so volunteers know exactly who&apos;s in the building today.</p>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            Live integrations available today. Beta platforms activate on request — we wire them up for your shelter, typically 1&ndash;3 business days.
          </p>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-fern text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-3 gap-8">
          <div>
            <ShieldCheck className="w-8 h-8 mb-3" strokeWidth={1.5} />
            <h3 className="font-heading text-xl font-semibold mb-2">Private by design</h3>
            <p className="text-white/85 text-sm">Volunteer data lives in your shelter&apos;s own database instance. No cross-shelter mixing.</p>
          </div>
          <div>
            <HeartHandshake className="w-8 h-8 mb-3" strokeWidth={1.5} />
            <h3 className="font-heading text-xl font-semibold mb-2">Built for nonprofits</h3>
            <p className="text-white/85 text-sm">Priced for shelter budgets. No per-volunteer fees. No enterprise sales calls.</p>
          </div>
          <div>
            <Users className="w-8 h-8 mb-3" strokeWidth={1.5} />
            <h3 className="font-heading text-xl font-semibold mb-2">Made with real staff</h3>
            <p className="text-white/85 text-sm">Every feature was requested by an actual volunteer coordinator managing real dogs, cats, and people.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="request" className="max-w-6xl mx-auto px-6 py-16 md:py-24 text-center">
        <p className="label-caps mb-2">Get in touch</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 max-w-2xl mx-auto">
          Ready to make Saturday morning easier?
        </h2>
        <p className="text-muted-foreground max-w-lg mx-auto mb-8">
          We&apos;re onboarding a small group of animal shelters this quarter. Send us a note and we&apos;ll get you set up.
        </p>
        <a href="mailto:hello@shelterfocus.app?subject=Shelter Focus%20interest&body=Hi%20—%20we're%20a%20shelter%20interested%20in%20Shelter%20Focus.%20Our%20details%3A%0A%0AShelter%20name%3A%20%0AState%2FRegion%3A%20%0AApprox.%20number%20of%20volunteers%3A%20%0AShelter%20software%20we%20use%20(if%20any)%3A%20%0A%0A—%20Sent%20from%20shelterfocus.app">
          <Button data-testid="cta-email" className="rounded-full h-12 px-8 bg-terracotta hover:bg-terracotta-dark text-white">
            Email us to learn more <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
          </Button>
        </a>
        <p className="text-xs text-muted-foreground mt-6">
          Or <Link to="/login" className="underline">sign in to your shelter&apos;s account</Link> if you&apos;re already onboarded.
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <PawPrint className="w-4 h-4 text-fern" strokeWidth={1.5} />
            <span>Shelter Focus © 2026</span>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-ink" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-ink" data-testid="footer-terms">Terms</Link>
            <a href="#request" className="hover:text-ink">Contact</a>
            <Link to="/login" className="hover:text-ink">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
